import { AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Pencil component `DCBiW` (`v2/ConfirmDialog`). Centered modal with title +
 * body + Cancel / destructive Confirm. Used by Cancel Booking, danger-zone
 * actions, and any irreversible operation.
 *
 * Backed by `@radix-ui/react-alert-dialog` (via the shadcn `AlertDialog`
 * primitive) — gives us focus trap, restore-focus-on-close,
 * portal-rendered overlay, and Escape-to-cancel for free. Prop signature
 * is preserved so existing call sites don't change.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "destructive",
  pending,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "destructive" | "primary";
  pending?: boolean;
}) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-start gap-4">
            <div
              className={cn(
                "h-10 w-10 rounded-full flex items-center justify-center shrink-0",
                tone === "destructive"
                  ? "bg-status-bg-cancelled text-status-cancelled"
                  : "bg-primary/10 text-primary",
              )}
            >
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <AlertDialogTitle className="text-base font-semibold tracking-tight">
                {title}
              </AlertDialogTitle>
              {description && (
                <AlertDialogDescription className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                  {description}
                </AlertDialogDescription>
              )}
            </div>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending} className={buttonVariants({ variant: "ghost", size: "sm" })}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            // Don't auto-close on click — let the parent decide based on
            // the mutation result. The parent flips `open` to false in
            // its own onSuccess.
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            className={buttonVariants({
              variant: tone === "destructive" ? "destructive" : "default",
              size: "sm",
            })}
          >
            {pending ? "Working…" : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
